import {alias, createModelSchema, deserialize, list, reference, serialize} from "serializr";
import {BaseObject} from "@/common/BaseObject";
import {Schema} from "@/common/Schema";

import {Range} from "../utils/Range";
import {ShipKind} from "./ShipKind";
import {ItemKey} from "../item/core/ItemKey";
import {Currency} from "../currency/Currency";
import {RewardModel} from "../reward/RewardModel";
import {GuardConfig} from "../guard/GuardConfig";

import {RedactorUtils} from "@/modules/redactor/core/RedactorUtils";
import {GuardRule} from "@/modules/redactor/models/guard/GuardRule";
import {TraderOfferGroup} from "@/modules/redactor/models/trader/TraderOfferGroup";


export class TraderOffer extends BaseObject {

    static schema() {
        return reference("uid", (uid, cb) => {
            return cb(null, RedactorUtils.getTraderOffer(uid));
        });
    }

    constructor() {
        super();

        this._key = null;
        this._name = null;

        this._group = null;
        this._guard = new GuardConfig();
        this._trader = ShipKind.NONE;
        this._quantity = 0;
        this._discount = 0;
        this._multiplier = 0;

        this._order = 0;
        this._price = [];
        this._reward = new RewardModel();
    }

    get key() {
        return this._key;
    }

    set key(value) {
        this._key = value;
    }

    get name() {
        return this._name;
    }

    set name(value) {
        this._name = value;
    }

    get guard() {
        return this._guard;
    }

    set guard(value) {
        this._guard = value;
    }

    get trader() {
        return this._trader;
    }

    set trader(value) {
        this._trader = value;
    }

    get quantity() {
        return this._quantity;
    }

    set quantity(value) {
        this._quantity = value;
    }

    get discount() {
        return this._discount;
    }

    set discount(value) {
        this._discount = value;
    }

    get multiplier() {
        return this._multiplier;
    }

    set multiplier(value) {
        this._multiplier = value;
    }

    get order() {
        return this._order;
    }

    set order(value) {
        this._order = value;
    }

    get group() {
        return this._group;
    }

    set group(value) {
        this._group = value;
    }

    get price() {
        return this._price;
    }

    set price(value) {
        this._price = value;
    }

    get reward() {
        return this._reward;
    }

    set reward(value) {
        this._reward = value;
    }

    valid() {

        if (!Number.isInteger(this.order) || this.order < 0) {
            return false;
        }

        if (!this.group || !TraderOfferGroup.is(this.group)) {
            return false;
        }

        if (!Number.isInteger(this.quantity) || this.quantity < 0) {
            return false;
        }

        if (!Number.isInteger(this.discount) || this.discount < 0 || this.discount > 100) {
            return false;
        }

        if (Number.isNaN(this.multiplier) || this.multiplier < 0) {
            return false;
        }

        if (this.key == null || this.key.length <= 0) {
            return false;
        }

        if (this.guard && !this.guard.valid()) {
            return false;
        }

        if (!ShipKind.is(this.trader) || ShipKind.NONE.equal(this.trader)) {
            return false;
        }

        if (this.price.length === 0 || this.price.some(v => !v || !v.valid())) {
            return false;
        }

        if (this.reward && !this.reward.valid()) {
            return false;
        }

        return true;
    }

    copy() {
        let value = super.copy();
        value._enabled = false;
        return value;
    }

    clone(target = undefined) {
        if (!target) {
            return super.clone(new TraderOffer());
        } else if (target instanceof TraderOffer) {
            return super.clone(target);
        } else {
            console.assert(false);
            return undefined;
        }
    }

    mergeFromJSON(data) {
        if (data.uid && this.uid !== data.uid) {
            throw "does not match uid: " + data.uid;
        }

        for (let key in data) {
            if (key === "key") {
                this.key = data[key];
            } else if (key === "name") {
                this.name = data[key];
            } else if (key === "enabled") {
                this.enabled = data[key];
            } else if (key === "quantity") {
                this.quantity = data[key];
            } else if (key === "discount") {
                this.discount = data[key];
            } else if (key === "multiplier") {
                this.multiplier = data[key];
            } else if (key === "order") {
                this.order = data[key];
            } else if (key === "group") {
                this.group = TraderOfferGroup.getValue(data[key]);
            } else if (key === "trader") {
                this.trader = ShipKind.getValue(data[key]);
            } else if (key === "price") {
                this.price = deserialize(Currency, data[key]);
            } else if (key === "guard_level") {
                this.guard.level = deserialize(Range, data[key]);
            } else if (key === "guard_allow") {
                this.guard.allow = deserialize(GuardRule, data[key]);
            } else if (key === "reward_items") {
                this.reward.items = deserialize(ItemKey, data[key]);
            } else if (key === "reward_money") {
                this.reward.money = deserialize(Currency, data[key]);
            }
        }
    }

    exportToJSON() {
        return {
            "uid": this.uid,
            "key": this.key,
            "name": this.name,
            "enabled": this.enabled ? 1 : 0,
            "quantity": this.quantity,
            "discount": this.discount,
            "multiplier": this.multiplier,

            "order": this.order,
            "group": this.group ? this.group.id : 0,
            "trader": this.trader ? this.trader.id : 0,
            "price": serialize(Currency, this.price),
            "guard_level": serialize(Range, this.guard.level),
            "guard_allow": serialize(GuardRule, this.guard.allow),
            "guard_block": serialize(GuardRule, this.guard.block),
            "reward_items": serialize(ItemKey, this.reward.items),
            "reward_money": serialize(Currency, this.reward.money)
        };
    }
}

createModelSchema(TraderOffer, {
    _key: alias("key", Schema.primitive()),
    _name: alias("name", Schema.primitive()),

    _group: alias("group", TraderOfferGroup.schema()),

    _guard: alias("guard", GuardConfig.schema()),
    _trader: alias("trader", ShipKind.schema()),
    _quantity: alias("quantity", Schema.primitive()),
    _discount: alias("discount", Schema.primitive()),
    _multiplier: alias("multiplier", Schema.primitive()),

    _order: alias("order", Schema.primitive()),
    _price: alias("price", list(Currency.schema())),
    _reward: alias("reward", RewardModel.schema())
});